import { defineStore } from 'pinia';
import { useUserStore } from './userStore';

interface DemoContact {
  email: string;
  phone: string;
  consentSms: boolean;
  consentEmail: boolean;
}

interface DemoState {
  isDemo: boolean;
  demoTenantId: string | null;
  demoAccessToken: string | null;
  demoContact: DemoContact | null;
  showOnboarding: boolean;
}

// Restore persisted demo session from localStorage (if any)
function loadPersistedSession(): Partial<DemoState> {
  try {
    const raw = localStorage.getItem('demoSession');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        parsed.isDemo &&
        parsed.demoTenantId &&
        parsed.demoAccessToken
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore corrupt data
  }
  return {};
}

function persistSession(state: DemoState) {
  localStorage.setItem(
    'demoSession',
    JSON.stringify({
      isDemo: state.isDemo,
      demoTenantId: state.demoTenantId,
      demoAccessToken: state.demoAccessToken
    })
  );
}

const saved = loadPersistedSession();

export const useDemoStore = defineStore('demo', {
  state: (): DemoState => ({
    isDemo: saved.isDemo || false,
    demoTenantId: saved.demoTenantId || null,
    demoAccessToken: saved.demoAccessToken || null,
    demoContact: null,
    showOnboarding: false
  }),

  getters: {
    isDemoMode: (state) => state.isDemo,
    hasContact: (state) => !!state.demoContact
  },

  actions: {
    // Call once at app startup to restore full demo state (userStore, CSS class, etc.)
    hydrateFromStorage() {
      if (!this.isDemo || !this.demoTenantId || !this.demoAccessToken) return;

      const userStore = useUserStore();
      userStore.setIdToken(this.demoAccessToken);

      // Restore contact
      const savedContact = localStorage.getItem('demoContact');
      if (savedContact) {
        try {
          this.demoContact = JSON.parse(savedContact);
          userStore.setUser({
            id: 'demo-user',
            simpleId: 'demo-user',
            tenantId: this.demoTenantId,
            name: this.demoContact!.email.split('@')[0],
            email: this.demoContact!.email,
            phone: this.demoContact!.phone
          });
        } catch (e) {
          console.error('Failed to parse saved demo contact', e);
        }
      }

      if (!userStore.user) {
        userStore.setUser({
          id: 'demo-user',
          simpleId: 'demo-user',
          tenantId: this.demoTenantId,
          name: 'Demo User',
          email: 'demo@myuser.ai',
          phone: ''
        });
      }
    },

    setDemo(tenantId: string, accessToken: string) {
      this.isDemo = true;
      this.demoTenantId = tenantId;
      this.demoAccessToken = accessToken;

      // Persist so it survives refresh
      persistSession(this.$state);

      // Propagate demo token to userStore so all API calls that
      // manually read userStore.idToken will use the demo token
      const userStore = useUserStore();
      userStore.setIdToken(accessToken);

      // Try to load contact from localStorage
      const savedContact = localStorage.getItem('demoContact');
      if (savedContact) {
        try {
          this.demoContact = JSON.parse(savedContact);
          // Also set the user in userStore from saved contact
          userStore.setUser({
            id: 'demo-user',
            simpleId: 'demo-user',
            tenantId: tenantId,
            name: this.demoContact!.email.split('@')[0],
            email: this.demoContact!.email,
            phone: this.demoContact!.phone
          });
        } catch (e) {
          console.error('Failed to parse saved demo contact', e);
        }
      }

      // If no contact yet, set a placeholder user so the app doesn't
      // show "Not logged in" — it'll be updated when they enter info
      if (!userStore.user) {
        userStore.setUser({
          id: 'demo-user',
          simpleId: 'demo-user',
          tenantId: tenantId,
          name: 'Demo User',
          email: 'demo@myuser.ai',
          phone: ''
        });
      }
    },

    setContact(contact: { email: string; phone: string }) {
      this.demoContact = {
        ...contact,
        consentSms: true,
        consentEmail: true
      };
      // Save to localStorage
      localStorage.setItem('demoContact', JSON.stringify(this.demoContact));
      this.showOnboarding = false;

      // Update userStore so navbar/avatar shows their real info
      const userStore = useUserStore();
      userStore.setUser({
        id: 'demo-user',
        simpleId: 'demo-user',
        tenantId: this.demoTenantId || '',
        name: contact.email.split('@')[0],
        email: contact.email,
        phone: contact.phone
      });
    },

    clearDemo() {
      this.isDemo = false;
      this.demoTenantId = null;
      this.demoAccessToken = null;
      this.demoContact = null;
      this.showOnboarding = false;
      localStorage.removeItem('demoContact');
      localStorage.removeItem('demoSession');

      // Clear the demo user from userStore
      const userStore = useUserStore();
      userStore.setUser(null);
      userStore.clearIdToken();
    }
  }
});
